import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Container, Card, Row, Col, Form, Button, Alert, Spinner, Badge, Table} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";
import {reportsApi} from "../../../api/reports.js";

function AdminReportsPage() {
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const loadReports = async () => {
        setIsLoading(true);
        setError("");
        try {
            const resp = await reportsApi.getOrderReports({
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined
            });
            setData(resp);
        } catch (e) {
            console.error("Failed to load reports", e);
            setError("Failed to load reports. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadReports();
    }, []);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Reports"
                title="Operations & Revenue"
                subtitle="Monitor order volume and revenue across customers and tax authorities."
                actions={
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" onClick={() => {setDateFrom(""); setDateTo(""); loadReports();}}>
                            Reset Dates
                        </Button>
                        <Button variant="primary" onClick={loadReports}>Refresh</Button>
                    </div>
                }
            />
            <Card className="dashboard-panel mt-3">
                <Card.Header className="bg-white"><strong>Filters</strong></Card.Header>
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label>Date From</Form.Label>
                            <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label>Date To</Form.Label>
                            <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </Col>
                        <Col md="auto">
                            <Button variant="primary" onClick={loadReports}>Apply</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {isLoading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading reports...</span>
                </div>
            )}
            {!isLoading && data && (
                <>
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Summary</strong>
                            <Badge bg="light" text="dark">{data.summary.totalOrders} orders</Badge>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3 text-center">
                                <Col md={3}>
                                    <div className="small text-muted">Revenue</div>
                                    <div className="h4">${data.summary.totalRevenue.toFixed(2)}</div>
                                </Col>
                                <Col md={3}>
                                    <div className="small text-muted">Product Fees</div>
                                    <div className="h5">${data.summary.totalProductFees.toFixed(2)}</div>
                                </Col>
                                <Col md={3}>
                                    <div className="small text-muted">Service Fees</div>
                                    <div className="h5">${data.summary.totalServiceFees.toFixed(2)}</div>
                                </Col>
                                <Col md={3}>
                                    <div className="small text-muted">Tax Collected</div>
                                    <div className="h6">${data.summary.totalTax.toFixed(2)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Orders by Tax Authority</strong>
                            <Badge bg="light" text="dark">{data.byTaxAuthority.rows.length} authorities</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Tax Authority</th>
                                        <th className="text-end">Orders</th>
                                        <th className="text-end">Revenue</th>
                                        <th className="text-end">Product Fees</th>
                                        <th className="text-end">Service Fees</th>
                                        <th className="text-end">Tax</th>
                                        <th className="text-end">Last Order</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.byTaxAuthority.rows.map((row) => (
                                        <tr key={row.taxAuthorityId}>
                                            <td>{row.taxAuthorityName}</td>
                                            <td className="text-end">{row.orders}</td>
                                            <td className="text-end">${row.revenue.toFixed(2)}</td>
                                            <td className="text-end">${row.productFees.toFixed(2)}</td>
                                            <td className="text-end">${row.serviceFees.toFixed(2)}</td>
                                            <td className="text-end">${row.tax.toFixed(2)}</td>
                                            <td className="text-end">{row.lastOrderDate ? new Date(row.lastOrderDate).toLocaleDateString() : "-"}</td>
                                        </tr>
                                    ))}
                                    {!data.byTaxAuthority.rows.length && (
                                        <tr><td colSpan={7} className="text-center text-muted py-3">No data</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Orders by Day</strong>
                            <Badge bg="light" text="dark">{data.byDay.rows.length} days</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th className="text-end">Orders</th>
                                        <th className="text-end">Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.byDay.rows.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{new Date(row.date).toLocaleDateString()}</td>
                                            <td className="text-end">{row.orders}</td>
                                            <td className="text-end">${row.revenue.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    {!data.byDay.rows.length && (
                                        <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </>
            )}
        </Container>
    );
}

export default observer(AdminReportsPage);
